import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import {
  TrendingUp, TrendingDown, Minus, Search, Plus, X, Pencil, Trash2,
  ShieldAlert, Loader2, AlertTriangle, Sparkles, Settings2,
} from "lucide-react";
import { useDemoMode } from "@/lib/demoMode";
import ContextualImportButton from "@/components/ContextualImportButton";
import ContextButton from "@/components/ContextButton";
import ContextScopeBanner from "@/components/ContextScopeBanner";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import DataStateTrendBadge from "@/components/DataStateTrendBadge";
import AnnuaireEntitySelector from "@/components/AnnuaireEntitySelector";
import ProactiveWhisper from "@/components/ProactiveWhisper";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import { ShareToChatButton } from "@/components/ShareToChat";

const RISQUES_IMPORT_FIELDS = [
  { key: "title", label: "Titre", required: true },
  { key: "categorie", label: "Catégorie", default: "Autre" },
  { key: "niveau", label: "Niveau", default: "tactique" },
  { key: "owner", label: "Owner", default: "" },
  { key: "code", label: "Code", default: "" },
  { key: "impact_brut", label: "Impact brut", type: "int", default: 3 },
  { key: "probabilite_brute", label: "Probabilité brute", type: "int", default: 3 },
  { key: "impact_net", label: "Impact net", type: "int", default: 2 },
  { key: "probabilite_nette", label: "Probabilité nette", type: "int", default: 2 },
];

function color(score) {
  if (score >= 12) return "var(--risk-high)";
  if (score >= 6) return "var(--risk-medium)";
  return "var(--risk-low)";
}

function tend(t) {
  if (t === "haussiere") return <TrendingUp className="w-3.5 h-3.5 text-[#E24B4A]" />;
  if (t === "baissiere") return <TrendingDown className="w-3.5 h-3.5 text-[#1D9E75]" />;
  return <Minus className="w-3.5 h-3.5 text-zinc-500" />;
}

const NIVEAU_KEYS = ["strategique", "tactique", "operationnel"];
const TENDANCE_KEYS = ["stable", "haussiere", "baissiere"];
const CATEGORY_KEYS = [
  "Sécurité de l'information",
  "Conformité réglementaire",
  "Continuité d'activité",
  "Ressources humaines",
  "Gouvernance des données",
  "Financier",
  "Stratégique",
  "Opérationnel",
];

function formatErr(detail) {
  if (!detail) return "Erreur.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Risques() {
  const { t } = useTranslation();
  const isDemo = useDemoMode();
  const endpoint = isDemo ? "/risks" : "/workspace/risks";
  const linkBase = isDemo ? "/demo/risques" : "/app/risques";

  const [risks, setRisks] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [mitConfig, setMitConfig] = useState({ forte: 2, moyenne: 1, faible: 0 });
  const [showConfig, setShowConfig] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [error, setError] = useState(null);
  const [ctxScope, setCtxScope] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const impactFilter = searchParams.get("impact");
  const probaFilter = searchParams.get("proba");
  const levelFilter = searchParams.get("level"); // "critical" | "high" | "low"

  const load = async () => {
    try {
      const r = await api.get(endpoint);
      setRisks(r.data);
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail));
      setRisks([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  useEffect(() => {
    if (isDemo) return;
    api.get(`${endpoint}/mitigation-config`)
      .then((r) => setMitConfig(r.data.config))
      .catch(() => {});
    // eslint-disable-next-line
  }, [endpoint, isDemo]);

  // V23.15.9 — Prefill depuis l'IA Copilote (Intelligence V2)
  // L'IA dépose `noam_prefill_risques` dans sessionStorage avant la navigation.
  // On consomme et on ouvre la modale en mode create avec données préchargées.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("noam_prefill_risques");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      sessionStorage.removeItem("noam_prefill_risques");
      if (parsed && parsed.data && typeof parsed.data === "object") {
        setPrefill(parsed.data);
        setEditing("new");
      }
    } catch (e) {
      // Silently ignore corrupted prefill data
    }
  }, []);

  const filtered = (risks || []).filter((r) => {
    if (ctxScope && !ctxScope.ids.has(r.id)) return false;
    const matchesQ =
      r.title.toLowerCase().includes(q.toLowerCase()) ||
      (r.code || "").toLowerCase().includes(q.toLowerCase()) ||
      r.categorie.toLowerCase().includes(q.toLowerCase());
    if (!matchesQ) return false;
    if (impactFilter && Number(r.impact_net) !== Number(impactFilter)) return false;
    if (probaFilter && Number(r.probabilite_nette) !== Number(probaFilter)) return false;
    if (levelFilter) {
      const net = r.impact_net * r.probabilite_nette;
      if (levelFilter === "critical" && net < 12) return false;
      if (levelFilter === "high" && (net < 6 || net >= 12)) return false;
      if (levelFilter === "low" && net >= 6) return false;
    }
    return true;
  });

  const hasActiveFilter = !!(impactFilter || probaFilter || levelFilter);
  const clearMatrixFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("impact"); params.delete("proba"); params.delete("level");
    setSearchParams(params);
  };

  const remove = async (r) => {
    if (!window.confirm(t("saas.risques.delete_confirm", { title: r.title }))) return;
    try {
      await api.delete(`${endpoint}/${r.id}`);
      await load();
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="risques-root">
      {!isDemo && <ProactiveWhisper module="risques" />}
      <ThreeQPageHeader endpoint="/workspace/risques/top-priority" />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="noam-overline">{t("saas.risques.overline")}</div>
          <h1 className="noam-display text-2xl font-bold tracking-tight">{t("saas.risques.title")}</h1>
          <p className="text-zinc-400 text-sm mt-1">{t("saas.risques.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/grc-matrix" className="btn-ghost text-xs" data-testid="risques-grc-matrix-link">
            Matrice vivante & contrôles
          </Link>
          <ModuleHelpButton moduleKey="risques" />
          {!isDemo && (
            <button
              onClick={() => setShowConfig(true)}
              className="btn-ghost text-xs"
              title={t("saas.risques.config.btn")}
              data-testid="risk-mitigation-config-btn"
            >
              <Settings2 className="w-3.5 h-3.5" /> {t("saas.risques.config.btn")}
            </button>
          )}
          <div className="flex items-center gap-2 noam-card px-3 py-2 w-72">
            <Search className="w-4 h-4 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("saas.risques.search_placeholder")}
              className="bg-transparent text-sm outline-none w-full"
              data-testid="risk-search"
            />
          </div>
          {!isDemo && <ContextButton moduleKey="risques" />}
          {!isDemo && (
            <ContextualImportButton
              moduleKey="risques"
              targetFields={RISQUES_IMPORT_FIELDS}
              onImported={async () => { await load(); }}
            />
          )}
          {!isDemo && (
            <button onClick={() => setEditing("new")} className="btn-primary text-xs" data-testid="risk-create-btn">
              <Plus className="w-3.5 h-3.5" /> {t("saas.risques.new_btn")}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs" data-testid="risques-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
          <span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      <ContextScopeBanner module="risques" onScope={setCtxScope} />

      {hasActiveFilter && (
        <div className="flex items-center gap-2 p-2.5 rounded-md border border-[#378ADD]/30 bg-[#378ADD]/10 text-[#378ADD] text-xs" data-testid="risques-filter-banner">
          <span className="font-medium">Filtre actif :</span>
          {impactFilter && <span className="px-2 py-0.5 bg-white/5 rounded">Impact = {impactFilter}</span>}
          {probaFilter && <span className="px-2 py-0.5 bg-white/5 rounded">Probabilité = {probaFilter}</span>}
          {levelFilter && <span className="px-2 py-0.5 bg-white/5 rounded">{t("saas.axp2.risk_level_f", { v: levelFilter === "critical" ? t("saas.axp2.risk_critical") : levelFilter === "high" ? t("saas.axp2.risk_high") : t("saas.axp2.risk_low") })}</span>}
          <button onClick={clearMatrixFilter} className="ml-auto text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="risques-filter-clear">
            <X className="w-3 h-3" /> Effacer
          </button>
        </div>
      )}

      {risks === null && (
        <div className="noam-card p-10 text-center text-zinc-500 text-sm flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> {t("saas.risques.loading")}
        </div>
      )}

      {risks && risks.length === 0 && (
        <EmptyState isDemo={isDemo} onCreate={() => setEditing("new")} />
      )}

      {risks && risks.length > 0 && (
        <RiskMatrix risks={risks} searchParams={searchParams} setSearchParams={setSearchParams} />
      )}

      {risks && risks.length > 0 && (
        <div className="noam-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 border-b border-white/5 bg-white/[0.02]">
              <tr>
                <th className="text-left py-3 px-4 font-normal">{t("saas.risques.table.code")}</th>
                <th className="text-left font-normal">{t("saas.risques.table.title")}</th>
                <th className="text-left font-normal hidden md:table-cell">{t("saas.risques.table.categorie")}</th>
                <th className="text-left font-normal hidden lg:table-cell">{t("saas.risques.table.owner")}</th>
                <th className="text-center font-normal">{t("saas.risques.table.brut")}</th>
                <th className="text-center font-normal">{t("saas.risques.table.net")}</th>
                <th className="text-center font-normal hidden lg:table-cell">{t("saas.risques.table.maitrise")}</th>
                <th className="text-center font-normal">{t("saas.risques.table.tendance")}</th>
                <th className="text-center font-normal">{t("saas.risques.table.controles")}</th>
                <th className="text-center font-normal">{t("saas.risques.table.plans")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const brut = r.impact_brut * r.probabilite_brute;
                const net = r.impact_net * r.probabilite_nette;
                return (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02] group" data-testid={`risque-row-${r.id}`}>
                    <td className="py-3 px-4 font-mono text-xs text-zinc-400">{r.code}</td>
                    <td className="text-white">
                      {r.title}
                      {r.provenance && (
                        <span className="block text-[10px] text-[#7F77DD]" title={`« ${r.provenance.excerpt || ""} »`} data-testid={`risque-provenance-${r.id}`}>
                          {t("saas.axp2.risk_source")} : {r.provenance.document_name || r.provenance.source_label || "document"} · {t("saas.axp2.risk_validated_by")} {"" + (r.provenance.proposal_id ? t("saas.axp2.risk_human_review") : t("saas.axp2.risk_review"))}
                        </span>
                      )}
                    </td>
                    <td className="text-zinc-400 hidden md:table-cell">{t(`saas.risques.categories.${r.categorie}`, { defaultValue: r.categorie })}</td>
                    <td className="text-zinc-400 hidden lg:table-cell">{r.owner || "—"}</td>
                    <td className="text-center">
                      <span className="inline-flex items-center justify-center w-8 h-6 rounded text-xs font-semibold"
                        style={{ background: `${color(brut)}26`, color: color(brut) }}>
                        {brut}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="inline-flex items-center justify-center w-8 h-6 rounded text-xs font-semibold"
                        style={{ background: `${color(net)}26`, color: color(net) }}>
                        {net}
                      </span>
                    </td>
                    <td className="text-center hidden lg:table-cell">
                      <MaitriseBadge value={r.maitrise} />
                    </td>
                    <td className="text-center">
                      {(() => {
                        const tNorm = r.tendance === "haussiere" ? "hausse" : r.tendance === "baissiere" ? "baisse" : "stable";
                        const etat = net >= 12 ? "critique" : net >= 6 ? "attention" : "bon";
                        return <DataStateTrendBadge data={net} etat={etat} tendance={tNorm} polarity="negative" compact />;
                      })()}
                    </td>
                    <td className="text-center text-zinc-300">{r.controles}</td>
                    <td className="text-center text-zinc-300">{r.plans_action}</td>
                    <td className="text-right pr-4">
                      <div className="flex items-center justify-end gap-1">
                        {!isDemo && (
                          <>
                            <button
                              onClick={() => setEditing(r)}
                              className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white opacity-0 group-hover:opacity-100"
                              title={t("saas.risques.edit_tooltip")}
                              data-testid={`risque-edit-${r.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => remove(r)}
                              className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-[#E24B4A] opacity-0 group-hover:opacity-100"
                              title={t("saas.risques.delete_tooltip")}
                              data-testid={`risque-delete-${r.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        <ShareToChatButton
                          attachment={{ kind: "risque", id: r.id }}
                          title={r.title}
                          className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-[#1D9E75] opacity-0 group-hover:opacity-100"
                        />
                        <Link to={`${linkBase}/${r.id}`} className="text-xs text-[#378ADD]" data-testid={`risque-detail-${r.id}`}>
                          {t("saas.risques.detail_link")}
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={11} className="py-12 text-center text-zinc-500 text-sm">{t("saas.risques.no_match")}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <RiskFormModal
          endpoint={endpoint}
          risk={editing === "new" ? null : editing}
          prefill={editing === "new" ? prefill : null}
          mitConfig={mitConfig}
          onClose={() => { setEditing(null); setPrefill(null); }}
          onSaved={async () => { setEditing(null); setPrefill(null); await load(); }}
        />
      )}

      {showConfig && (
        <MitigationConfigModal
          endpoint={endpoint}
          config={mitConfig}
          onClose={() => setShowConfig(false)}
          onSaved={async (cfg) => { setShowConfig(false); setMitConfig(cfg); await load(); }}
        />
      )}
    </div>
  );
}

function EmptyState({ isDemo, onCreate }) {
  const { t } = useTranslation();
  if (isDemo) {
    return (
      <div className="noam-card p-12 text-center" data-testid="risques-empty-demo">
        <div className="text-zinc-400">{t("saas.risques.no_risks_demo")}</div>
      </div>
    );
  }
  return (
    <div className="noam-card p-10 text-center" data-testid="risques-empty">
      <div className="w-14 h-14 mx-auto rounded-xl bg-[#378ADD]/10 border border-[#378ADD]/30 flex items-center justify-center">
        <ShieldAlert className="w-6 h-6 text-[#378ADD]" />
      </div>
      <h2 className="noam-display text-xl font-semibold mt-4">{t("saas.risques.empty_title")}</h2>
      <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
        {t("saas.risques.empty_help")}
      </p>
      <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
        <button onClick={onCreate} className="btn-primary text-sm" data-testid="risques-empty-create">
          <Plus className="w-4 h-4" /> {t("saas.risques.empty_create")}
        </button>
        <Link to="/app/configurer" className="btn-ghost text-sm" data-testid="risques-empty-assistant">
          <Sparkles className="w-4 h-4" /> {t("saas.risques.empty_assistant")}
        </Link>
      </div>
    </div>
  );
}

function RiskFormModal({ endpoint, risk, prefill, mitConfig, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!risk;
  const p = prefill || {};
  const [form, setForm] = useState({
    title: risk?.title ?? p.title ?? "",
    code: risk?.code ?? p.code ?? "",
    categorie: risk?.categorie ?? p.categorie ?? CATEGORY_KEYS[0],
    niveau: risk?.niveau ?? p.niveau ?? "tactique",
    owner: risk?.owner ?? p.owner ?? "",
    impact_brut: risk?.impact_brut ?? p.impact_brut ?? p.impact_net ?? 3,
    probabilite_brute: risk?.probabilite_brute ?? p.probabilite_brute ?? p.probabilite_nette ?? 3,
    impact_net: risk?.impact_net ?? p.impact_net ?? 2,
    probabilite_nette: risk?.probabilite_nette ?? p.probabilite_nette ?? 2,
    maitrise: risk ? (risk.maitrise ?? "") : (p.maitrise ?? "faible"),
    impacts_brut: risk?.impacts_brut ?? null,
    tendance: risk?.tendance ?? p.tendance ?? "stable",
    controles: risk?.controles ?? 0,
    plans_action: risk?.plans_action ?? 0,
    cout_inaction: risk?.cout_inaction ?? 0,
    annuaire_entity_ids: risk?.annuaire_entity_ids || [],
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const update = (k) => (e) => {
    const v = e?.target?.value ?? e;
    const n = ["impact_brut", "probabilite_brute", "impact_net", "probabilite_nette", "controles", "plans_action", "cout_inaction"].includes(k);
    setForm((f) => ({ ...f, [k]: n ? Number(v) : v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(null);
    const payload = { ...form, maitrise: form.maitrise || null, impact_brut: effImpactBrut, impact_net: netI, probabilite_nette: netP };
    try {
      if (isEdit) {
        await api.patch(`${endpoint}/${risk.id}`, payload);
      } else {
        await api.post(endpoint, payload);
      }
      onSaved();
    } catch (er) {
      setErr(formatErr(er?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const dims = mitConfig?.impact_dimensions || [];
  const aggMode = mitConfig?.impact_aggregation || "max";
  const dimVals = Object.values(form.impacts_brut || {});
  const effImpactBrut = dimVals.length
    ? (aggMode === "moyenne"
        ? Math.max(1, Math.min(5, Math.round(dimVals.reduce((a, b) => a + b, 0) / dimVals.length)))
        : Math.max(...dimVals))
    : form.impact_brut;
  const toggleDim = (id) => {
    setForm((f) => {
      const cur = { ...(f.impacts_brut || {}) };
      if (id in cur) delete cur[id];
      else cur[id] = 3;
      return { ...f, impacts_brut: Object.keys(cur).length ? cur : null };
    });
  };
  const setDimVal = (id) => (e) => {
    const v = Number(e.target.value);
    setForm((f) => ({ ...f, impacts_brut: { ...(f.impacts_brut || {}), [id]: v } }));
  };

  const brut = effImpactBrut * form.probabilite_brute;
  const off = form.maitrise ? (mitConfig?.[form.maitrise] ?? 0) : 0;
  const netI = form.maitrise ? Math.max(1, effImpactBrut - off) : form.impact_net;
  const netP = form.maitrise ? Math.max(1, form.probabilite_brute - off) : form.probabilite_nette;
  const net = netI * netP;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="risk-form-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline">{isEdit ? t("saas.risques.form.edit_label") : t("saas.risques.form.new_label")}</div>
            <div className="noam-display text-base font-semibold mt-0.5">{isEdit ? risk.title : t("saas.risques.form.create_title")}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="overflow-y-auto p-5 space-y-4">
          {prefill && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#EF9F27]/10 border border-[#EF9F27]/30 text-[11px] text-[#EF9F27]" data-testid="risk-form-prefill-banner">
              <Sparkles className="w-3.5 h-3.5 shrink-0" />
              Pré-rempli par l&apos;IA Copilote — vérifiez et ajustez avant de créer.
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{t("saas.risques.form.title_label")}</label>
              <input required value={form.title} onChange={update("title")} className="noam-input" placeholder={t("saas.risques.form.title_ph")} data-testid="risk-form-title" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{t("saas.risques.form.code_label")}</label>
              <input value={form.code} onChange={update("code")} className="noam-input font-mono" placeholder="RSK-CYB-001" data-testid="risk-form-code" />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{t("saas.risques.form.categorie_label")}</label>
              <select value={form.categorie} onChange={update("categorie")} className="noam-input" data-testid="risk-form-categorie">
                {CATEGORY_KEYS.map((c) => <option key={c} value={c}>{t(`saas.risques.categories.${c}`) || c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{t("saas.risques.form.niveau_label")}</label>
              <select value={form.niveau} onChange={update("niveau")} className="noam-input" data-testid="risk-form-niveau">
                {NIVEAU_KEYS.map((k) => <option key={k} value={k}>{t(`saas.risques.niveaux.${k}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{t("saas.risques.form.owner_label")}</label>
              <input value={form.owner} onChange={update("owner")} className="noam-input" placeholder={t("saas.risques.form.owner_ph")} data-testid="risk-form-owner" />
            </div>
          </div>

          <div className="border-t border-white/5 pt-4">
            <div className="text-[11px] text-zinc-500 uppercase tracking-wider mb-2">{t("saas.risques.form.section_brut_net")}</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="text-xs text-zinc-300 font-medium">{t("saas.risques.form.brut_section")}</div>
                <div className="flex items-center gap-3">
                  {dimVals.length > 0 ? (
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1">
                        <span>{t("saas.risques.form.impact")}</span>
                        <span className="text-white font-semibold" data-testid="risk-form-impact-computed">
                          {effImpactBrut} <span className="text-zinc-500 font-normal">({aggMode === "moyenne" ? t("saas.risques.config.agg_moyenne") : "MAX"})</span>
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full bg-[#378ADD]" style={{ width: `${(effImpactBrut / 5) * 100}%` }} />
                      </div>
                    </div>
                  ) : (
                    <NumberSlider label={t("saas.risques.form.impact")} value={form.impact_brut} onChange={update("impact_brut")} testid="risk-form-impact-brut" />
                  )}
                  <NumberSlider label={t("saas.risques.form.probabilite")} value={form.probabilite_brute} onChange={update("probabilite_brute")} testid="risk-form-prob-brute" />
                  <ScoreBadge value={brut} />
                </div>
                {dims.length > 0 && (
                  <div className="pt-1">
                    <div className="text-[10px] text-zinc-500 mb-1.5">{t("saas.risques.form.impact_dims_hint")}</div>
                    <div className="space-y-1.5">
                      {dims.map((d) => {
                        const active = !!(form.impacts_brut && d.id in form.impacts_brut);
                        return (
                          <div key={d.id} className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => toggleDim(d.id)}
                              className={`text-[10px] px-2 py-1 rounded border transition-colors w-24 text-left truncate ${active ? "border-[#378ADD]/50 bg-[#378ADD]/15 text-[#378ADD] font-semibold" : "border-white/10 bg-white/[0.03] text-zinc-600 hover:text-zinc-400"}`}
                              title={active ? t("saas.risques.form.dim_deactivate") : t("saas.risques.form.dim_activate")}
                              data-testid={`risk-form-dim-${d.id}`}
                            >
                              {d.label}
                            </button>
                            {active && (
                              <>
                                <input
                                  type="range" min={1} max={5} step={1}
                                  value={form.impacts_brut[d.id]}
                                  onChange={setDimVal(d.id)}
                                  className="flex-1 accent-[#378ADD]"
                                  data-testid={`risk-form-dim-slider-${d.id}`}
                                />
                                <span className="text-xs text-white font-semibold w-4 text-center">{form.impacts_brut[d.id]}</span>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <div className="text-xs text-zinc-300 font-medium">{t("saas.risques.form.net_section")}</div>
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{t("saas.risques.form.maitrise_label")}</label>
                  <select value={form.maitrise} onChange={update("maitrise")} className="noam-input" data-testid="risk-form-maitrise">
                    <option value="">{t("saas.risques.form.maitrise_manual")}</option>
                    {["forte", "moyenne", "faible"].map((k) => (
                      <option key={k} value={k}>
                        {t(`saas.risques.maitrise.${k}`)} (−{mitConfig?.[k] ?? 0})
                      </option>
                    ))}
                  </select>
                </div>
                {form.maitrise ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 text-[11px] text-zinc-400" data-testid="risk-form-net-computed">
                      {t("saas.risques.form.net_computed", { impact: netI, proba: netP })}
                    </div>
                    <ScoreBadge value={net} />
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <NumberSlider label={t("saas.risques.form.impact")} value={form.impact_net} onChange={update("impact_net")} testid="risk-form-impact-net" />
                    <NumberSlider label={t("saas.risques.form.probabilite")} value={form.probabilite_nette} onChange={update("probabilite_nette")} testid="risk-form-prob-nette" />
                    <ScoreBadge value={net} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-3 border-t border-white/5 pt-4">
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{t("saas.risques.form.tendance")}</label>
              <select value={form.tendance} onChange={update("tendance")} className="noam-input" data-testid="risk-form-tendance">
                {TENDANCE_KEYS.map((k) => <option key={k} value={k}>{t(`saas.risques.tendances.${k}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{t("saas.risques.form.controles")}</label>
              <input type="number" min={0} value={form.controles} onChange={update("controles")} className="noam-input" data-testid="risk-form-controles" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{t("saas.risques.form.plans_action")}</label>
              <input type="number" min={0} value={form.plans_action} onChange={update("plans_action")} className="noam-input" data-testid="risk-form-plans" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{t("saas.risques.form.cout_inaction")}</label>
              <input type="number" min={0} step={50000} value={form.cout_inaction} onChange={update("cout_inaction")} className="noam-input" data-testid="risk-form-cout" />
            </div>
          </div>

          <div className="border-t border-white/5 pt-4">
            <AnnuaireEntitySelector
              value={form.annuaire_entity_ids || []}
              onChange={(ids) => setForm((f) => ({ ...f, annuaire_entity_ids: ids }))}
              testid="risk-entity-selector"
              label={t("saas.risques.form.entities_label", { defaultValue: "Acteurs exposés ou contributeurs (Annuaire)" })}
              placeholder={t("saas.risques.form.entities_ph", { defaultValue: "Rechercher contact, holding, fournisseur…" })}
            />
          </div>

          {err && (
            <div className="flex items-start gap-2 p-2.5 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs" data-testid="risk-form-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {err}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5 sticky bottom-0 bg-[var(--noam-bg)]">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">{t("saas.risques.form.cancel")}</button>
            <button type="submit" disabled={saving} className="btn-primary text-xs disabled:opacity-50" data-testid="risk-form-submit">
              {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("saas.risques.form.saving")}</> : isEdit ? t("saas.risques.form.submit_update") : t("saas.risques.form.submit_create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NumberSlider({ label, value, onChange, testid }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1">
        <span>{label}</span>
        <span className="text-white font-semibold">{value}</span>
      </div>
      <input
        type="range"
        min={1}
        max={5}
        step={1}
        value={value}
        onChange={onChange}
        className="w-full accent-[#378ADD]"
        data-testid={testid}
      />
    </div>
  );
}

function ScoreBadge({ value }) {
  return (
    <div
      className="w-10 h-10 rounded-md flex items-center justify-center font-bold text-sm shrink-0"
      style={{ background: `${color(value)}26`, color: color(value) }}
    >
      {value}
    </div>
  );
}

const MAITRISE_COLORS = { forte: "#1D9E75", moyenne: "#EF9F27", faible: "#E24B4A" };

function hexColor(score) {
  if (score >= 12) return "#E24B4A";
  if (score >= 6) return "#EF9F27";
  return "#1D9E75";
}

function MaitriseBadge({ value }) {
  const { t } = useTranslation();
  if (!value) return <span className="text-zinc-600 text-xs">—</span>;
  const c = MAITRISE_COLORS[value] || "#71717a";
  return (
    <span
      className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap"
      style={{ background: `${c}1f`, color: c }}
      data-testid={`maitrise-badge-${value}`}
    >
      {t(`saas.risques.maitrise.${value}`)}
    </span>
  );
}

function RiskMatrix({ risks, searchParams, setSearchParams }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState("net");
  const cells = {};
  risks.forEach((r) => {
    const p = mode === "net" ? r.probabilite_nette : r.probabilite_brute;
    const i = mode === "net" ? r.impact_net : r.impact_brut;
    const k = `${p}-${i}`;
    cells[k] = (cells[k] || 0) + 1;
  });
  const selImpact = searchParams.get("impact");
  const selProba = searchParams.get("proba");
  const clickCell = (p, i) => {
    if (mode !== "net") return;
    const params = new URLSearchParams(searchParams);
    if (selImpact === String(i) && selProba === String(p)) {
      params.delete("impact"); params.delete("proba");
    } else {
      params.set("impact", String(i)); params.set("proba", String(p));
    }
    params.delete("level");
    setSearchParams(params);
  };
  return (
    <div className="noam-card p-5" data-testid="risques-matrix">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="noam-overline">{t("saas.risques.matrix.overline")}</div>
          <div className="noam-display text-base font-semibold">{t("saas.risques.matrix.title")}</div>
        </div>
        <div className="flex rounded-md border border-white/10 overflow-hidden text-xs">
          <button
            onClick={() => setMode("brut")}
            className={`px-3 py-1.5 transition-colors ${mode === "brut" ? "bg-[#378ADD]/20 text-[#378ADD] font-semibold" : "text-zinc-400 hover:text-white"}`}
            data-testid="matrix-mode-brut"
          >
            {t("saas.risques.matrix.brut")}
          </button>
          <button
            onClick={() => setMode("net")}
            className={`px-3 py-1.5 transition-colors ${mode === "net" ? "bg-[#378ADD]/20 text-[#378ADD] font-semibold" : "text-zinc-400 hover:text-white"}`}
            data-testid="matrix-mode-net"
          >
            {t("saas.risques.matrix.net")}
          </button>
        </div>
      </div>
      <div className="flex gap-2">
        <div className="flex items-center shrink-0">
          <span className="text-[10px] text-zinc-500 uppercase tracking-wider" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
            {t("saas.risques.matrix.axis_proba")}
          </span>
        </div>
        <div className="flex-1 min-w-0 max-w-2xl">
          <div className="grid grid-cols-5 gap-1.5">
            {[5, 4, 3, 2, 1].map((p) =>
              [1, 2, 3, 4, 5].map((i) => {
                const n = cells[`${p}-${i}`] || 0;
                const sc = p * i;
                const selected = mode === "net" && selImpact === String(i) && selProba === String(p);
                return (
                  <button
                    key={`${p}-${i}`}
                    onClick={() => clickCell(p, i)}
                    className={`h-10 rounded border border-white/5 flex items-center justify-center transition-all ${mode === "net" ? "hover:scale-[1.04] hover:border-white/20 cursor-pointer" : "cursor-default"} ${selected ? "ring-2 ring-white/70" : ""} ${n === 0 ? "opacity-50" : ""}`}
                    style={{ background: `${hexColor(sc)}1A` }}
                    title={`P${p} × I${i} = ${sc}`}
                    data-testid={`risques-matrix-cell-${p}-${i}`}
                  >
                    {n > 0 && (
                      <span
                        className="text-[11px] font-semibold rounded-full w-6 h-6 flex items-center justify-center"
                        style={{ background: hexColor(sc), color: "#fff" }}
                      >
                        {n}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
          <div className="flex justify-between mt-1.5 px-1">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("saas.risques.matrix.axis_impact")} →</span>
            <span className="text-[10px] text-zinc-600">{mode === "net" ? t("saas.risques.matrix.hint_net") : t("saas.risques.matrix.hint_brut")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MitigationConfigModal({ endpoint, config, onClose, onSaved }) {
  const { t } = useTranslation();
  const [vals, setVals] = useState({ forte: config.forte, moyenne: config.moyenne, faible: config.faible });
  const [dims, setDims] = useState((config.impact_dimensions || []).map((d) => ({ ...d })));
  const [agg, setAgg] = useState(config.impact_aggregation || "max");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      const payload = {
        forte: Number(vals.forte), moyenne: Number(vals.moyenne), faible: Number(vals.faible),
        impact_aggregation: agg,
        impact_dimensions: dims
          .filter((d) => (d.label || "").trim())
          .map((d) => ({ id: d.id || slugify(d.label), label: d.label.trim() })),
      };
      const r = await api.put(`${endpoint}/mitigation-config`, payload);
      onSaved(r.data.config);
    } catch (er) {
      setErr(formatErr(er?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="mitigation-config-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline">{t("saas.risques.config.overline")}</div>
            <div className="noam-display text-base font-semibold mt-0.5">{t("saas.risques.config.title")}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-4">
          <p className="text-xs text-zinc-400">{t("saas.risques.config.desc")}</p>
          {["forte", "moyenne", "faible"].map((k) => (
            <div key={k} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <MaitriseBadge value={k} />
                <span className="text-xs text-zinc-400">{t(`saas.risques.config.${k}_hint`)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-zinc-500 text-sm">−</span>
                <input
                  type="number" min={0} max={4}
                  value={vals[k]}
                  onChange={(e) => setVals((v) => ({ ...v, [k]: e.target.value }))}
                  className="noam-input w-16 text-center"
                  data-testid={`mitigation-config-${k}`}
                />
              </div>
            </div>
          ))}
          <p className="text-[11px] text-zinc-500">{t("saas.risques.config.hint")}</p>
          <div className="border-t border-white/5 pt-3 space-y-2">
            <div className="text-xs text-zinc-300 font-medium">{t("saas.risques.config.dims_title")}</div>
            <p className="text-[11px] text-zinc-500">{t("saas.risques.config.dims_desc")}</p>
            {dims.map((d, i) => (
              <div key={d.id || i} className="flex items-center gap-2">
                <input
                  value={d.label}
                  onChange={(e) => setDims((arr) => arr.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                  className="noam-input flex-1 text-sm"
                  data-testid={`mitigation-dim-label-${i}`}
                />
                <button type="button" onClick={() => setDims((arr) => arr.filter((_, j) => j !== i))}
                  className="p-1.5 rounded text-zinc-500 hover:text-[#E24B4A] hover:bg-white/5"
                  data-testid={`mitigation-dim-delete-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => setDims((arr) => [...arr, { id: "", label: "" }])}
              className="btn-ghost text-xs" data-testid="mitigation-dim-add">
              <Plus className="w-3.5 h-3.5" /> {t("saas.risques.config.dim_add")}
            </button>
            <div className="flex items-center justify-between gap-3 pt-1">
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider">{t("saas.risques.config.agg_label")}</label>
              <select value={agg} onChange={(e) => setAgg(e.target.value)} className="noam-input w-56" data-testid="mitigation-agg-select">
                <option value="max">{t("saas.risques.config.agg_max")}</option>
                <option value="moyenne">{t("saas.risques.config.agg_moyenne_full")}</option>
              </select>
            </div>
          </div>
          {err && (
            <div className="flex items-start gap-2 p-2.5 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs" data-testid="mitigation-config-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {err}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">{t("saas.risques.form.cancel")}</button>
            <button type="submit" disabled={saving} className="btn-primary text-xs disabled:opacity-50" data-testid="mitigation-config-save">
              {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("saas.risques.form.saving")}</> : t("saas.risques.config.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
